import React, { useEffect, useState } from 'react';
import type { GoogleReviewsComponent } from '../../../types/builder';
import { Star } from 'lucide-react';

interface GoogleReviewsRendererProps {
  component: GoogleReviewsComponent;
}

export const GoogleReviewsRenderer: React.FC<GoogleReviewsRendererProps> = ({ component }) => {
  const { settings } = component;
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!settings.autoPlay || settings.reviews.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % settings.reviews.length);
    }, settings.autoPlaySpeed || 5000);

    return () => clearInterval(interval);
  }, [settings.autoPlay, settings.autoPlaySpeed, settings.reviews.length]);

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            fill={i < rating ? settings.starColor : 'transparent'}
            stroke={i < rating ? settings.starColor : '#d1d5db'}
            strokeWidth={2}
          />
        ))}
      </div>
    );
  };

  if (settings.reviews.length === 0) {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          padding: `${settings.padding}px`,
        }}
      >
        <div className="text-center py-8 text-gray-500">
          No reviews to display
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: settings.backgroundColor,
        color: settings.textColor,
        padding: `${settings.padding}px`,
      }}
    >
      <div className="max-w-2xl mx-auto">
        {settings.title && (
          <h2 className="text-xl font-bold mb-6 text-center">{settings.title}</h2>
        )}

        {/* Carousel Container */}
        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
            }}
          >
            {settings.reviews.map((review) => (
              <div
                key={review.id}
                className="w-full flex-shrink-0 px-2"
              >
                <div
                  style={{
                    backgroundColor: settings.cardBackgroundColor,
                    color: settings.textColor,
                  }}
                  className="rounded-lg p-6 shadow-md"
                >
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-4">
                    {review.authorImage ? (
                      <img
                        src={review.authorImage}
                        alt={review.authorName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                        {review.authorName.charAt(0).toUpperCase()}
                      </div>
                    )}

                    <div className="flex-1">
                      <h3 className="font-semibold text-base">{review.authorName}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {renderStars(review.rating)}
                        <span className="text-xs opacity-70">{review.date}</span>
                      </div>
                    </div>

                    {/* Google Logo */}
                    <svg width="24" height="24" viewBox="0 0 48 48">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      <path fill="none" d="M0 0h48v48H0z"/>
                    </svg>
                  </div>

                  {/* Review Text */}
                  <p className="text-sm leading-relaxed">{review.reviewText}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Indicators */}
        {settings.reviews.length > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {settings.reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'w-6 bg-blue-600'
                    : 'bg-gray-400 hover:bg-gray-500'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

